import React, { useEffect, useState, Component } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useBluelagoon } from "@bluelagoon";
import { track } from "@crm/analytics";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@shadcn-in-nx/ui/dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import { Label } from "@shadcn-in-nx/ui/label";
import { Input } from "@shadcn-in-nx/ui/input";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import Message from "./Message";
import LanguageSwitcher from "./LanguageSwitcher";
import { ExternalLink, ChevronRight, ChevronLeft } from "lucide-react";

// Error boundary to prevent crashes from breaking the app
class TermsAcceptanceErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.log("TermsAcceptance error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            // Render nothing if there's an error
            return null;
        }
        return this.props.children;
    }
}

const CURRENT_TERMS_VERSION = "1.0"; // Update this when terms version changes

const TermsAcceptance = () => {
    const { t } = useTranslation();
    const { user, getUser } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [role, setRole] = useState("");
    const [customRole, setCustomRole] = useState("");
    const [industry, setIndustry] = useState("");
    const [customIndustry, setCustomIndustry] = useState("");
    const [buildingGoal, setBuildingGoal] = useState("");
    const customInputRef = React.useRef(null);
    const contentRef = React.useRef(null);

    const {
        call: acceptTerms,
        error: termsError,
        isLoading: isAcceptingTerms,
    } = useBluelagoon({
        method: "acceptTerms",
    });

    const {
        call: updateUser,
        error: updateError,
        isLoading: isUpdatingUser,
    } = useBluelagoon({
        method: "updateUser",
    });

    const { call: aiRecommend } = useBluelagoon({
        method: "aiRecommendBlueprint",
    });

    const error = termsError || updateError;
    const isLoading = isAcceptingTerms || isUpdatingUser;

    const TOTAL_STEPS = 3;

    const ROLE_OPTIONS = [
        t("termsAcceptance.roles.owner"),
        t("termsAcceptance.roles.productManager"),
        t("termsAcceptance.roles.consultant"),
        t("termsAcceptance.roles.developer"),
        t("termsAcceptance.roles.businessAnalyst"),
        t("termsAcceptance.roles.designer"),
        t("termsAcceptance.roles.dataAnalyst"),
        t("termsAcceptance.roles.operationsManager"),
        t("termsAcceptance.roles.sales"),
        t("termsAcceptance.roles.marketing"),
        t("termsAcceptance.roles.customerSupport"),
        t("termsAcceptance.roles.freelancer"),
        t("termsAcceptance.roles.student"),
        t("termsAcceptance.roles.other"),
    ];

    const INDUSTRY_OPTIONS = [
        t("termsAcceptance.industries.technology"),
        t("termsAcceptance.industries.healthcare"),
        t("termsAcceptance.industries.finance"),
        t("termsAcceptance.industries.retail"),
        t("termsAcceptance.industries.manufacturing"),
        t("termsAcceptance.industries.education"),
        t("termsAcceptance.industries.realEstate"),
        t("termsAcceptance.industries.legal"),
        t("termsAcceptance.industries.mediaEntertainment"),
        t("termsAcceptance.industries.transportation"),
        t("termsAcceptance.industries.agriculture"),
        t("termsAcceptance.industries.nonProfit"),
        t("termsAcceptance.industries.government"),
        t("termsAcceptance.industries.other"),
    ];

    useEffect(() => {
        if (user) {
            // Check if user needs to accept terms
            const needsToAcceptTerms =
                !user?.termsAccepted ||
                user?.termsAccepted?.version !== CURRENT_TERMS_VERSION ||
                !user?.termsAccepted?.isValid;

            if (needsToAcceptTerms) {
                setIsOpen(true);
            }
        }
    }, [user]);

    // Track when custom input should be shown
    const showCustomRoleInput = role === t("termsAcceptance.roles.other");
    const showCustomIndustryInput =
        industry === t("termsAcceptance.industries.other");

    // Scroll custom input into view when it appears on mobile
    useEffect(() => {
        if (
            customInputRef.current &&
            (showCustomRoleInput || showCustomIndustryInput)
        ) {
            // Small delay to ensure the input is rendered
            setTimeout(() => {
                customInputRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }, 100);
        }
    }, [showCustomRoleInput, showCustomIndustryInput]);

    const scrollToTop = () => {
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const handleNext = () => {
        if (currentStep < TOTAL_STEPS - 1) {
            setCurrentStep(currentStep + 1);
            scrollToTop();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            scrollToTop();
        }
    };

    const canProceedToNext = () => {
        switch (currentStep) {
            case 0: {
                // Role question
                const finalRole =
                    role === t("termsAcceptance.roles.other")
                        ? customRole
                        : role;
                return !!finalRole;
            }
            case 1: {
                // Industry question
                const finalIndustry =
                    industry === t("termsAcceptance.industries.other")
                        ? customIndustry
                        : industry;
                return !!finalIndustry;
            }
            case 2: // Building goal
                return buildingGoal.trim().length > 0;
            default:
                return false;
        }
    };

    const handleAcceptTerms = async (e) => {
        e.preventDefault();

        const finalRole =
            role === t("termsAcceptance.roles.other") ? customRole : role;
        const finalIndustry =
            industry === t("termsAcceptance.industries.other")
                ? customIndustry
                : industry;

        if (!finalRole || !finalIndustry || !buildingGoal.trim()) {
            return;
        }

        try {
            let language = "en"; // Default fallback
            try {
                const storedLanguage = localStorage.getItem("i18nextLng");
                if (storedLanguage) {
                    language = storedLanguage.split("-")[0];
                }
            } catch (storageError) {
                // localStorage might be unavailable (private mode, disabled, etc.)
                // Fall back to default "en"
                console.log(
                    "localStorage unavailable, using default language:",
                    storageError
                );
            }
            // Get UTM parameters from localStorage if available
            let utmParams = {};
            try {
                const storedUtm = localStorage.getItem("utm_params");
                if (storedUtm) {
                    const parsed = JSON.parse(storedUtm);
                    utmParams = {
                        utm_source: parsed.utm_source || undefined,
                        utm_medium: parsed.utm_medium || undefined,
                        utm_campaign: parsed.utm_campaign || undefined,
                        utm_term: parsed.utm_term || undefined,
                        utm_content: parsed.utm_content || undefined,
                    };
                    // Remove undefined values
                    Object.keys(utmParams).forEach(
                        (key) =>
                            utmParams[key] === undefined &&
                            delete utmParams[key]
                    );
                }
            } catch (utmError) {
                console.log("Error reading UTM parameters:", utmError);
            }

            // Get user's timezone from browser
            let timezone = "America/New_York"; // Default fallback
            try {
                timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            } catch (tzError) {
                console.log("Could not detect timezone:", tzError);
            }

            // Update user with onboarding data and UTM parameters
            await updateUser({
                userId: user.id,
                role: finalRole,
                industry: finalIndustry,
                buildingGoal: buildingGoal.trim(),
                language,
                timezone,
                ...utmParams,
            });

            // Accept terms
            await acceptTerms({
                version: CURRENT_TERMS_VERSION,
            });

            // Fire-and-forget AI blueprint recommendation
            aiRecommend({
                role: finalRole,
                industry: finalIndustry,
                buildingGoal: buildingGoal.trim(),
            })
                .then((res) => {
                    if (res?.data?.blueprint) {
                        localStorage.setItem(
                            "ai_recommended_blueprint",
                            JSON.stringify(res.data)
                        );
                        window.dispatchEvent(new Event("ai-blueprint-ready"));
                    }
                })
                .catch(() => {});

            // Track onboarding completion
            track("Onboarding Completed", {
                role: finalRole,
                industry: finalIndustry,
                language,
                termsVersion: CURRENT_TERMS_VERSION,
            });

            await getUser();
            setIsOpen(false);
        } catch (error) {
            console.log("Error accepting terms:", error);
            setIsOpen(false);
        }
    };

    const renderProgressTimeline = () => {
        return (
            <div className="w-full mb-4 sm:mb-6 flex-shrink-0">
                {/* Progress bar */}
                <div className="relative w-full h-1 bg-gray-200 rounded-full mb-2 sm:mb-4">
                    <div
                        className="absolute top-0 left-0 h-full bg-primary transition-all duration-300 rounded-full"
                        style={{
                            width: `${
                                ((currentStep + 1) / TOTAL_STEPS) * 100
                            }%`,
                        }}
                    />
                </div>

                {/* Step indicators - simplified on mobile */}
                <div className="flex items-center justify-between">
                    {[0, 1, 2].map((step) => {
                        const isActive = step === currentStep;
                        const isCompleted = step < currentStep;
                        return (
                            <div
                                key={step}
                                className="flex flex-col items-center flex-1"
                            >
                                <div
                                    className={`w-6 h-6 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-[10px] sm:text-sm font-medium transition-all duration-300 ${
                                        isActive
                                            ? "bg-primary text-primary-foreground scale-110"
                                            : isCompleted
                                            ? "bg-primary/80 text-primary-foreground"
                                            : "bg-gray-200 text-gray-500"
                                    }`}
                                >
                                    {isCompleted ? (
                                        <svg
                                            className="w-3 h-3 sm:w-5 sm:h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    ) : (
                                        step + 1
                                    )}
                                </div>
                                {/* Hide step labels on very small screens */}
                                <div className="mt-1 sm:mt-2 text-[10px] sm:text-sm text-center hidden xs:block sm:block">
                                    <span
                                        className={`${
                                            isActive
                                                ? "text-primary font-medium"
                                                : "text-gray-500"
                                        }`}
                                    >
                                        {t("termsAcceptance.step")} {step + 1}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div className="space-y-3 sm:space-y-4">
                        <Label className="text-base sm:text-xl font-semibold block">
                            {t("termsAcceptance.question1")}
                        </Label>
                        <div className="rounded-md border p-2 sm:p-4">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-3">
                                {ROLE_OPTIONS.map((option) => (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={() => setRole(option)}
                                        className={`px-2 py-2 sm:px-4 sm:py-3 text-[11px] sm:text-sm rounded-md border transition-all leading-tight ${
                                            role === option
                                                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                                : "bg-background hover:bg-accent hover:text-accent-foreground border-input"
                                        }`}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {role === t("termsAcceptance.roles.other") && (
                            <Input
                                ref={customInputRef}
                                placeholder={t(
                                    "termsAcceptance.customRolePlaceholder"
                                )}
                                value={customRole}
                                onChange={(e) => setCustomRole(e.target.value)}
                                className="mt-2"
                                autoFocus
                            />
                        )}
                    </div>
                );

            case 1:
                return (
                    <div className="space-y-3 sm:space-y-4">
                        <Label className="text-base sm:text-xl font-semibold block">
                            {t("termsAcceptance.question2")}
                        </Label>
                        <div className="rounded-md border p-2 sm:p-4">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-3">
                                {INDUSTRY_OPTIONS.map((option) => (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={() => setIndustry(option)}
                                        className={`px-2 py-2 sm:px-4 sm:py-3 text-[11px] sm:text-sm rounded-md border transition-all leading-tight ${
                                            industry === option
                                                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                                : "bg-background hover:bg-accent hover:text-accent-foreground border-input"
                                        }`}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {industry === t("termsAcceptance.industries.other") && (
                            <Input
                                ref={customInputRef}
                                placeholder={t(
                                    "termsAcceptance.customIndustryPlaceholder"
                                )}
                                value={customIndustry}
                                onChange={(e) =>
                                    setCustomIndustry(e.target.value)
                                }
                                className="mt-2"
                                autoFocus
                            />
                        )}
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-3 sm:space-y-4">
                        <Label className="text-base sm:text-xl font-semibold block">
                            {t("termsAcceptance.question3")}
                        </Label>
                        <Textarea
                            placeholder={t(
                                "termsAcceptance.buildingGoalPlaceholder"
                            )}
                            value={buildingGoal}
                            onChange={(e) => setBuildingGoal(e.target.value)}
                            rows={4}
                            className="resize-none min-h-[120px] sm:min-h-[200px]"
                        />
                        <p className="text-xs sm:text-sm text-muted-foreground">
                            {t("termsAcceptance.termsNotice")}{" "}
                            <a
                                href="https://www.agentui.ai/en/terms/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-600 inline-flex items-center gap-1 underline"
                            >
                                {t("termsAcceptance.termsLink")}
                                <ExternalLink size={12} />
                            </a>
                        </p>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog open={isOpen}>
            <DialogContent className="sm:max-w-2xl max-h-[100dvh] sm:max-h-[90vh] h-[100dvh] sm:h-auto flex flex-col p-3 sm:p-6 [&>button]:hidden overflow-hidden">
                <DialogHeader className="pb-2 sm:pb-4 flex-shrink-0">
                    <div className="flex items-start justify-between gap-2 sm:gap-4">
                        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                            {currentStep > 0 && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleBack}
                                    disabled={isLoading}
                                    className="w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    <span className="sr-only">
                                        {t("termsAcceptance.back")}
                                    </span>
                                </Button>
                            )}
                            <div className="flex-1 min-w-0">
                                <DialogTitle className="text-base sm:text-xl">
                                    {t("termsAcceptance.title")}
                                </DialogTitle>
                                <DialogDescription className="text-xs sm:text-base mt-0.5 sm:mt-1 line-clamp-2 sm:line-clamp-none">
                                    {t("termsAcceptance.description")}
                                </DialogDescription>
                            </div>
                        </div>
                        <div className="relative flex-shrink-0">
                            <LanguageSwitcher />
                        </div>
                    </div>
                </DialogHeader>

                {/* Progress Timeline */}
                {renderProgressTimeline()}

                {/* Step Content */}
                <form
                    onSubmit={handleAcceptTerms}
                    className="flex-1 flex flex-col min-h-0"
                >
                    <div ref={contentRef} className="flex-1 overflow-y-auto mb-3 sm:mb-4 -mx-3 sm:mx-0 px-3 sm:px-0 overscroll-contain">
                        {renderStepContent()}
                    </div>

                    {error && error.message && (
                        <div className="mb-3 sm:mb-4 flex-shrink-0">
                            <Message error>{error.message}</Message>
                        </div>
                    )}

                    <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 flex-shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0">
                        {currentStep < TOTAL_STEPS - 1 ? (
                            <Button
                                type="button"
                                onClick={handleNext}
                                disabled={!canProceedToNext() || isLoading}
                                className="w-full sm:w-auto order-1 sm:order-2 ml-auto"
                            >
                                {t("termsAcceptance.next")}
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </Button>
                        ) : (
                            <Button
                                type="submit"
                                disabled={!canProceedToNext() || isLoading}
                                isLoading={isLoading}
                                className="w-full sm:w-auto order-1 sm:order-2 ml-auto"
                            >
                                {t("termsAcceptance.completeAndAcceptTerms")}
                            </Button>
                        )}
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

// Wrap with error boundary to prevent crashes
const TermsAcceptanceWithErrorBoundary = () => (
    <TermsAcceptanceErrorBoundary>
        <TermsAcceptance />
    </TermsAcceptanceErrorBoundary>
);

export default TermsAcceptanceWithErrorBoundary;
